import {Badge, Box, Button, Drawer, Field, HStack, Icon, Input, Portal, Text, VStack} from "@chakra-ui/react"
import {IoCloseSharp} from "react-icons/io5"
import {useEffect, useState} from "react"
import {flattenBodySchema} from "@/pages/scenario/utils/bodySchemaUtils"

const EndpointParamsModal = ({isOpen, onClose, onSave, endpoint, initialValues = {}, initialBodyValues = {}}) => {
    const [values, setValues] = useState({})
    const [bodyValues, setBodyValues] = useState({})
    const [objectMode, setObjectMode] = useState({})

    const getObjectPaths = (nodes, prefix = "") => {
        const paths = []
        for (const node of nodes) {
            const p = prefix ? `${prefix}.${node.key}` : node.key
            if (node.type === "object" && node.children?.length) {
                paths.push(p)
                paths.push(...getObjectPaths(node.children, p))
            }
        }
        return paths
    }

    useEffect(() => {
        if (!endpoint) return
        const init = {}
        endpoint.params?.forEach(p => {
            init[p.name] = initialValues[p.name] ?? p.defaultValue ?? ""
        })
        setValues(init)

        const objectModeInit = {}
        getObjectPaths(endpoint.bodySchema ?? []).forEach(p => {
            if (initialBodyValues[p] !== undefined && initialBodyValues[p] !== "") {
                objectModeInit[p] = true
            }
        })
        setObjectMode(objectModeInit)

        const bodyInit = {}
        Object.keys(objectModeInit).forEach(p => { bodyInit[p] = initialBodyValues[p] })
        flattenBodySchema(endpoint.bodySchema ?? []).forEach(f => {
            const underWhole = Object.keys(objectModeInit).some(op => f.path.startsWith(op + "."))
            if (!underWhole) bodyInit[f.path] = initialBodyValues[f.path] ?? f.defaultValue ?? ""
        })
        setBodyValues(bodyInit)
    }, [endpoint, isOpen])

    const toggleObjectMode = (path, children) => {
        const isCurrentlyWhole = !!objectMode[path]
        if (isCurrentlyWhole) {
            setObjectMode(prev => { const next = {...prev}; delete next[path]; return next })
            setBodyValues(prev => {
                const next = {...prev}
                delete next[path]
                flattenBodySchema(children, path).forEach(f => { next[f.path] = f.defaultValue ?? "" })
                return next
            })
        } else {
            setObjectMode(prev => ({...prev, [path]: true}))
            setBodyValues(prev => {
                const next = {...prev}
                flattenBodySchema(children, path).forEach(f => { delete next[f.path] })
                next[path] = ""
                return next
            })
        }
    }

    const renderBodySchemaNodes = (nodes, prefix = "", depth = 0) =>
        nodes.map(node => {
            const path = prefix ? `${prefix}.${node.key}` : node.key
            if (node.type === "object" && node.children?.length) {
                const isWhole = !!objectMode[path]
                return (
                    <Box key={path} w="full" pl={depth * 4} borderLeft={depth > 0 ? "2px solid" : undefined} borderColor="gray.700">
                        <HStack mb={2} mt={depth > 0 ? 2 : 0} justifyContent="space-between">
                            <Text color="gray.400" fontSize={12} fontWeight="semibold" textTransform="uppercase" letterSpacing="wide">
                                {node.key}
                            </Text>
                            <Button size="xs" variant="ghost" color="gray.400" onClick={() => toggleObjectMode(path, node.children)}>
                                {isWhole ? "Expand fields" : "Map as object"}
                            </Button>
                        </HStack>
                        {isWhole ? (
                            <Input
                                variant="primary"
                                placeholder={`Value for ${node.key} (object)`}
                                value={bodyValues[path] ?? ""}
                                onChange={(e) => setBodyValues(prev => ({...prev, [path]: e.target.value}))}
                            />
                        ) : (
                            <VStack w="full" gap={3} alignItems="flex-start">
                                {renderBodySchemaNodes(node.children, path, depth + 1)}
                            </VStack>
                        )}
                    </Box>
                )
            }
            return (
                <Field.Root key={path} w="full" pl={depth * 4}>
                    <HStack gap={2} mb={1}>
                        <Field.Label color="white" fontSize={15} mb={0}>{node.key}</Field.Label>
                        {node.type && <Badge colorPalette="blue" size="sm">{node.type}</Badge>}
                        {node.required && <Badge colorPalette="red" size="sm">required</Badge>}
                    </HStack>
                    <Input
                        variant="primary"
                        placeholder={`Value for ${node.key}`}
                        value={bodyValues[path] ?? ""}
                        onChange={(e) => setBodyValues(prev => ({...prev, [path]: e.target.value}))}
                    />
                </Field.Root>
            )
        })

    const handleSave = () => {
        onSave?.(endpoint.id, values, bodyValues)
        onClose()
    }

    return (
        <Drawer.Root open={isOpen} onOpenChange={(e) => !e.open && onClose()} size={'md'} placement={'end'}>
            <Portal>
                <Drawer.Backdrop/>
                <Drawer.Positioner padding="4">
                    <Drawer.Content bg={"bg.drawer"} borderRadius={16}>
                        <Drawer.Header>
                            <HStack justifyContent={'space-between'} w={'full'}>
                                <VStack alignItems={'flex-start'} gap={0}>
                                    <HStack>
                                        <Text color={'white'} fontWeight={'semibold'}>{endpoint?.name}</Text>
                                        <Badge colorPalette={'subtle'} size={'sm'}>{endpoint?.provider}</Badge>
                                    </HStack>
                                    <Text color={'gray.400'} fontSize={13}>#{endpoint?.tag}</Text>
                                </VStack>
                                <Icon fontSize={32} color={'text.grey'} onClick={onClose} cursor={'pointer'}>
                                    <IoCloseSharp/>
                                </Icon>
                            </HStack>
                        </Drawer.Header>
                        <Drawer.Body>
                            <VStack w={'full'} gap={4} alignItems={'flex-start'}>
                                {endpoint?.params?.length > 0 ? (
                                    endpoint.params.map(param => (
                                        <Field.Root key={param.id} w={'full'}>
                                            <HStack gap={2} mb={1}>
                                                <Field.Label color={'white'} fontSize={15} mb={0}>
                                                    {param.name}
                                                </Field.Label>
                                                {param.type && (
                                                    <Badge colorPalette={'blue'} size={'sm'}>{param.type}</Badge>
                                                )}
                                                {param.required && (
                                                    <Badge colorPalette={'red'} size={'sm'}>required</Badge>
                                                )}
                                            </HStack>
                                            <Input
                                                variant={'primary'}
                                                placeholder={`Value for ${param.name}`}
                                                value={values[param.name] ?? ""}
                                                onChange={(e) => setValues(prev => ({...prev, [param.name]: e.target.value}))}
                                            />
                                        </Field.Root>
                                    ))
                                ) : (
                                    <Text color={'gray.500'}>This endpoint has no parameters.</Text>
                                )}
                                {endpoint?.bodySchema?.length > 0 && (
                                    <>
                                        <Text color={'white'} fontWeight={'semibold'} mt={2}>Request body</Text>
                                        {renderBodySchemaNodes(endpoint.bodySchema)}
                                    </>
                                )}
                            </VStack>
                        </Drawer.Body>
                        <Drawer.Footer borderRadius={16}>
                            <HStack justifyContent={'flex-end'} w={'full'} gap={3}>
                                <Button variant="outlined" textDecoration='unset' onClick={onClose}>Cancel</Button>
                                <Button variant="primary" textDecoration='unset' onClick={handleSave}>Save</Button>
                            </HStack>
                        </Drawer.Footer>
                    </Drawer.Content>
                </Drawer.Positioner>
            </Portal>
        </Drawer.Root>
    )
}

export default EndpointParamsModal
